import React, { useLayoutEffect, useRef } from 'react';
import { getDocument } from '@atlaskit/browser-apis';
import { fg } from '@atlaskit/platform-feature-flags';
export var ToolbarKeyboardNavigationProvider = function ToolbarKeyboardNavigationProvider(_ref) {
  var children = _ref.children,
    childComponentSelector = _ref.childComponentSelector,
    dom = _ref.dom,
    isShortcutToFocusToolbar = _ref.isShortcutToFocusToolbar,
    handleFocus = _ref.handleFocus,
    handleEscape = _ref.handleEscape,
    ariaLabel = _ref.ariaLabel,
    ariaControls = _ref.ariaControls;
  var wrapperRef = useRef(null);
  useLayoutEffect(function () {
    if (!wrapperRef.current) {
      return;
    }
    var element = wrapperRef.current;
    var getFocusableElements = function getFocusableElements() {
      if (!element) {
        return [];
      }
      // Find all focusable elements within the toolbar that match the child component selector
      var focusableSelectors = ['button:not([disabled])', '[role="button"]:not([disabled])', '[tabindex]:not([tabindex="-1"])'].join(',');
      var allFocusable = Array.from(element.querySelectorAll(focusableSelectors));

      // Filter to only include elements that are:
      // 1. Within the child component selector
      // 2. Visible (not hidden by display:none on itself or any parent)
      return allFocusable.filter(function (el) {
        if (!el.closest("".concat(childComponentSelector))) {
          return false;
        }

        // Check if the element or any of its parents have display: none
        var currentEl = el;
        while (currentEl && currentEl !== element) {
          var computedStyle = window.getComputedStyle(currentEl);
          if (computedStyle.display === 'none' || computedStyle.visibility === 'hidden') {
            return false;
          }
          currentEl = currentEl.parentElement;
        }
        return true;
      });
    };
    var moveFocus = function moveFocus(direction) {
      var _focusableElements$ne;
      var focusableElements = getFocusableElements();
      if (focusableElements.length === 0) {
        return;
      }
      var doc = getDocument();
      var currentIndex = focusableElements.findIndex(function (el) {
        return el === (doc === null || doc === void 0 ? void 0 : doc.activeElement);
      });
      var nextIndex;
      if (currentIndex === -1) {
        // No element currently focused, focus the first one
        nextIndex = 0;
      } else if (direction === 'right') {
        // Move right, wrap to beginning if at end
        nextIndex = (currentIndex + 1) % focusableElements.length;
      } else {
        // Move left, wrap to end if at beginning
        nextIndex = currentIndex === 0 ? focusableElements.length - 1 : currentIndex - 1;
      }
      (_focusableElements$ne = focusableElements[nextIndex]) === null || _focusableElements$ne === void 0 || _focusableElements$ne.focus();
    };
    var handleTab = function handleTab() {
      var focusableElements = getFocusableElements();
      if (focusableElements.length === 0) {
        return;
      }
      var doc = getDocument();
      var index = doc !== null && doc !== void 0 && doc.activeElement ? focusableElements.findIndex(function (el) {
        return el === (doc === null || doc === void 0 ? void 0 : doc.activeElement);
      }) : 0;
      if (index < 0) {
        index = 0;
      }
      focusableElements.forEach(function (el) {
        return el.setAttribute('tabindex', '-1');
      });
      focusableElements[index].setAttribute('tabindex', '0');
    };
    var handleKeyDown = function handleKeyDown(event) {
      var targetElement = event.target;
      if (targetElement instanceof HTMLElement && !targetElement.closest("".concat(childComponentSelector))) {
        return;
      }
      if (fg('platform_editor_toolbar_aifc_patch_7')) {
        switch (event.key) {
          case 'Escape':
            handleEscape(event);
            break;
          case 'ArrowLeft':
            event.preventDefault();
            moveFocus('left');
            break;
          case 'ArrowRight':
            event.preventDefault();
            moveFocus('right');
            break;
          case 'Tab':
            {
              handleTab();
              break;
            }
          default:
        }
      } else {
        switch (event.key) {
          case 'Escape':
            handleEscape(event);
            break;
          default:
        }
      }
    };
    var globalKeyDownHandler = function globalKeyDownHandler(event) {
      if (isShortcutToFocusToolbar && isShortcutToFocusToolbar(event)) {
        handleFocus(event);
      }
    };

    // ignored via go/ees005
    // eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
    element === null || element === void 0 || element.addEventListener('keydown', handleKeyDown);
    // ignored via go/ees005
    // eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
    dom === null || dom === void 0 || dom.addEventListener('keydown', globalKeyDownHandler);
    return function () {
      // ignored via go/ees005
      // eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
      element === null || element === void 0 || element.removeEventListener('keydown', handleKeyDown);
      // ignored via go/ees005
      // eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
      dom === null || dom === void 0 || dom.removeEventListener('keydown', globalKeyDownHandler);
    };
  }, [wrapperRef, childComponentSelector, dom, isShortcutToFocusToolbar, handleFocus, handleEscape]);
  return /*#__PURE__*/React.createElement("div", {
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- ignored via go/DSP-18766
    className: "custom-key-handler-wrapper",
    ref: wrapperRef,
    "aria-label": ariaLabel,
    "aria-controls": ariaControls
  }, children);
};